import React, { useEffect, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, MailCheck } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import { PhoneInput } from "../../components/PhoneInput";
import { SearchableSelect } from "../../components/SearchableSelect";
import { CodeInput } from "../../components/CodeInput";

const AUTH_IMAGE = "/bg2.jpeg";

export default function RegisterPage() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { signupInitiate, signupVerify, isAuthenticated, loading } = useAuth();
    const [step, setStep] = useState("form"); // "form" | "code"
    const [countries, setCountries] = useState([]);
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [code, setCode] = useState("");
    const [resendCount, setResendCount] = useState(0);
    const [f, setF] = useState({
        first_name: "", last_name: "", email: "",
        phone_code: "+1", phone: "",
        country: "", state: "", city: "",
        zip_code: "", address: "", password: "",
    });

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/api/countries");
            setCountries(data || []);
        })();
    }, []);

    // Redirect after all hooks have run (Rules of Hooks)
    if (!loading && isAuthenticated) return <Navigate to="/bookings" replace />;

    const submitForm = async (e) => {
        e.preventDefault();
        if (f.password.length < 6) return toast.error(t("auth.password_too_short"));
        setBusy(true);
        try {
            await signupInitiate({
                email: f.email,
                password: f.password,
                first_name: f.first_name,
                last_name: f.last_name,
                phone_code: f.phone_code,
                phone: f.phone,
                address: f.address,
                state: f.state,
                zip_code: f.zip_code,
                city: f.city,
                country: f.country,
                preferred_language: i18n.language || "en",
            });
            setStep("code");
            toast.success(t("auth.code_sent"));
        } catch (err) {
            const status = err?.response?.status;
            if (status === 409) toast.error(t("auth.email_already_exists"));
            else if (status === 429) toast.error(t("auth.too_many_requests"));
            else toast.error(t("auth.generic_error"));
        } finally {
            setBusy(false);
        }
    };

    const submitCode = async (e) => {
        e.preventDefault();
        if (code.length !== 6) return;
        setBusy(true);
        try {
            await signupVerify(f.email, code);
            toast.success(t("auth.account_created"));
            navigate("/bookings", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail || "";
            if (detail.toLowerCase().includes("invalid code")) toast.error(t("auth.invalid_code"));
            else if (detail.toLowerCase().includes("expired")) toast.error(t("auth.code_expired"));
            else if (detail.toLowerCase().includes("attempts")) toast.error(t("auth.too_many_attempts"));
            else if (detail.toLowerCase().includes("already")) toast.error(t("auth.email_already_exists"));
            else toast.error(t("common.error"));
        } finally {
            setBusy(false);
        }
    };

    const resend = async () => {
        if (resendCount >= 2) return toast.error(t("auth.too_many_requests"));
        try {
            await signupInitiate({
                email: f.email, password: f.password, first_name: f.first_name, last_name: f.last_name,
                phone_code: f.phone_code, phone: f.phone, address: f.address, state: f.state,
                zip_code: f.zip_code, city: f.city, country: f.country,
                preferred_language: i18n.language || "en",
            });
            setResendCount(resendCount + 1);
            toast.success(t("auth.code_resent"));
        } catch {
            toast.error(t("auth.too_many_requests"));
        }
    };

    return (
        <div data-testid="register-page" className="min-h-[calc(100vh-4rem)] grid md:grid-cols-2">
            <div className="flex items-center justify-center p-6 md:p-12">
                <div className="w-full max-w-md space-y-6">
                    {step === "form" ? (
                        <>
                            <div className="space-y-2">
                                <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
                                    {t("auth.register_title")}
                                </h1>
                                <p className="text-muted-foreground font-medium">{t("auth.register_subtitle")}</p>
                            </div>
                            <form onSubmit={submitForm} className="space-y-3" data-testid="register-form">
                                <div className="grid grid-cols-2 gap-3">
                                    <I label={t("auth.first_name")} required val={f.first_name} set={(v) => setF({ ...f, first_name: v })} testId="register-first-name" />
                                    <I label={t("auth.last_name")} required val={f.last_name} set={(v) => setF({ ...f, last_name: v })} testId="register-last-name" />
                                </div>
                                <I label={t("auth.email")} type="email" required val={f.email} set={(v) => setF({ ...f, email: v })} testId="register-email" />
                                <PhoneInput
                                    label={t("auth.phone")} required
                                    phoneCode={f.phone_code} phone={f.phone}
                                    onChange={({ phone_code, phone }) => setF({ ...f, phone_code, phone })}
                                    testPrefix="register-phone"
                                />
                                <div>
                                    <label className="text-sm font-semibold block mb-1.5">{t("auth.country")} *</label>
                                    <SearchableSelect testId="register-country"
                                        options={countries.map((c) => ({ value: c.name, label: c.name }))}
                                        value={f.country} onValueChange={(v) => setF({ ...f, country: v })}
                                        placeholder={t("common.choose")} searchPlaceholder={t("common.search")} />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <I label={t("auth.state")} val={f.state} set={(v) => setF({ ...f, state: v })} testId="register-state" />
                                    <I label={t("auth.city")} val={f.city} set={(v) => setF({ ...f, city: v })} testId="register-city" />
                                </div>
                                <I label={t("auth.address")} val={f.address} set={(v) => setF({ ...f, address: v })} testId="register-address" />
                                <I label={t("auth.zip_code")} val={f.zip_code} set={(v) => setF({ ...f, zip_code: v })} testId="register-zip" />
                                <div>
                                    <label className="text-sm font-semibold block mb-1.5">{t("auth.password")} *</label>
                                    <div className="relative">
                                        <input data-testid="register-password" type={show ? "text" : "password"} required minLength={6}
                                            value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })}
                                            className="input-modern pr-12" />
                                        <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>
                                <button type="submit" data-testid="register-submit" disabled={busy} className="btn-primary w-full disabled:opacity-50">
                                    {busy ? t("common.loading") : t("auth.register")}
                                </button>
                            </form>
                            <div className="text-center text-sm">
                                <span className="text-muted-foreground">{t("auth.have_account")} </span>
                                <Link to="/login" data-testid="register-to-login" className="font-bold underline">
                                    {t("auth.sign_in_now")}
                                </Link>
                            </div>
                        </>
                    ) : (
                        <>
                            <button type="button" onClick={() => { setStep("form"); setCode(""); }}
                                className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground"
                                data-testid="register-code-back">
                                <ArrowLeft className="h-4 w-4" />
                                {t("common.back")}
                            </button>
                            <div className="space-y-2">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-xs font-semibold">
                                    <MailCheck className="h-3.5 w-3.5" />
                                    {t("auth.code_sent_short")}
                                </div>
                                <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
                                    {t("auth.enter_code_title")}
                                </h1>
                                <p className="text-muted-foreground font-medium">
                                    {t("auth.enter_code_subtitle", { email: f.email })}
                                </p>
                            </div>
                            <form onSubmit={submitCode} className="space-y-6">
                                <CodeInput value={code} onChange={setCode} testIdPrefix="register-code" />
                                <button type="submit" data-testid="register-code-submit" disabled={busy || code.length !== 6} className="btn-primary w-full disabled:opacity-50">
                                    {busy ? t("common.loading") : t("auth.verify")}
                                </button>
                                <div className="text-center text-sm">
                                    <button type="button" data-testid="register-code-resend" onClick={resend} className="font-semibold underline-offset-4 hover:underline">
                                        {t("auth.resend_code")}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
            <div className="hidden md:block relative">
                <img src={AUTH_IMAGE} alt="concert" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-br from-black/30 to-black/70" />
                <div className="absolute bottom-10 left-10 right-10 text-white">
                    <img src="/logo-youvibeticket-wht.png" alt="youvibeticket" className="h-12 w-auto mb-2" />
                    <p className="text-white/80">{t("brand.tagline")}</p>
                </div>
            </div>
        </div>
    );
}

const I = ({ label, required, type = "text", val, set, testId }) => (
    <div>
        <label className="text-sm font-semibold block mb-1.5">{label}{required ? " *" : ""}</label>
        <input data-testid={testId} type={type} required={required} value={val || ""}
            onChange={(e) => set(e.target.value)} className="input-modern" />
    </div>
);
